/**
 * Wishlist System for Shopify
 * Stores wishlist items in localStorage for logged-in customers.
 */

const WISHLIST_STORAGE_KEY = 'shopify_wishlist';

class Wishlist {
  constructor() {
    this.isCustomerLoggedIn = window.octoberCustomerLoggedIn === true;
    this.items = this.load();
    this.init();
  }

  load() {
    if (!this.isCustomerLoggedIn) return [];

    try {
      const stored = localStorage.getItem(WISHLIST_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Failed to load wishlist from localStorage', e);
      return [];
    }
  }

  save() {
    if (!this.isCustomerLoggedIn) return;

    try {
      localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(this.items));
      this.notify();
    } catch (e) {
      console.error('Failed to save wishlist to localStorage', e);
    }
  }

  notify() {
    const event = new CustomEvent('wishlist:updated', { detail: { items: this.items } });
    document.dispatchEvent(event);
    this.updateButtons();
    this.updateCountBadge();
  }

  add(productId, productHandle) {
    if (!this.isCustomerLoggedIn) {
      this.promptLogin();
      return;
    }

    if (!this.items.some((item) => item.id === productId)) {
      this.items.push({ id: productId, handle: productHandle, added_at: new Date().getTime() });
      this.save();
    }
  }

  remove(productId) {
    if (!this.isCustomerLoggedIn) return;

    this.items = this.items.filter((item) => item.id !== productId);
    this.save();
  }

  toggle(productId, productHandle) {
    if (!this.isCustomerLoggedIn) {
      this.promptLogin();
      return;
    }

    if (this.isInWishlist(productId)) {
      this.remove(productId);
    } else {
      this.add(productId, productHandle);
    }
  }

  promptLogin() {
    const loginUrl = window.octoberCustomerLoginUrl || '/account/login';
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
    const destination = `${loginUrl}?return_url=${returnUrl}`;

    if (window.octoberActionNotice && typeof window.octoberActionNotice.confirm === 'function') {
      window.octoberActionNotice.confirm({
        title: 'Sign in to save wishlist items',
        message: 'Wishlist is available for signed-in customers. Sign in to save pieces and view them later.',
        confirmLabel: 'Sign in',
        cancelLabel: 'Not now',
        onConfirm: () => {
          window.location.href = destination;
        },
      });
      return;
    }

    window.location.href = destination;
  }

  isInWishlist(productId) {
    return this.items.some((item) => String(item.id) === String(productId));
  }

  updateButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
      const id = button.dataset.wishlistId;
      if (this.isInWishlist(id)) {
        button.classList.add('is-active');
        button.setAttribute('aria-pressed', 'true');
      } else {
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
      }

      if (!this.isCustomerLoggedIn) {
        button.setAttribute('title', 'Sign in to save to wishlist');
        button.setAttribute('aria-label', 'Sign in to save to wishlist');
      }
    });
  }

  updateCountBadge() {
    const badges = document.querySelectorAll('[data-wishlist-count]');
    badges.forEach((badge) => {
      badge.textContent = this.items.length;
      badge.style.display = this.items.length > 0 ? 'flex' : 'none';
    });
  }

  init() {
    document.addEventListener('DOMContentLoaded', () => {
      this.updateButtons();
      this.updateCountBadge();

      document.addEventListener('click', (e) => {
        const toggleBtn = e.target.closest('[data-wishlist-toggle]');
        if (toggleBtn) {
          e.preventDefault();
          this.toggle(toggleBtn.dataset.wishlistId, toggleBtn.dataset.wishlistHandle);
        }
      });
    });
  }
}

window.wishlist = new Wishlist();
